import i18nInstance from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import de from './de.json';
import en from './en.json';
import nb from './nb.json';
import { TenantConfig } from '../types/tenantConfig';

let htmlLangSyncRegistered = false;

export const initializeI18n = async (tenantConfig: TenantConfig) => {
    // Keep the document language in sync with the active i18n language so screen
    // readers use the correct pronunciation rules (WCAG 3.1.1/3.1.2). Registered
    // before init so the languageChanged event fired during init is also caught.
    if (!htmlLangSyncRegistered && typeof document !== 'undefined') {
        i18nInstance.on('languageChanged', (lng) => {
            document.documentElement.lang = lng;
        });
        htmlLangSyncRegistered = true;
    }

    i18nInstance
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
            detection: {
                order: ['querystring', 'localStorage', 'navigator'],
            },
            resources: {
                de: { translations: de },
                en: { translations: en },
                nb: { translations: nb },
            },
            supportedLngs: tenantConfig.languages,
            fallbackLng: tenantConfig.fallbackLng ?? tenantConfig.languages[0],
            debug: true,
            ns: ['translations'],
            defaultNS: 'translations',
            interpolation: {
                escapeValue: false,
                format: (value, format) => {
                    if (typeof value === 'number' && format === 'number')
                        return formatNumber(value);
                    if (typeof value === 'number' && format === 'money')
                        return formatMoney(value, tenantConfig.currency);
                    return value;
                },
            },
        });
};

export const supportedLanguages = ['de', 'en', 'nb'];

export const i18n = i18nInstance;

export const persistLanguage = (language: string): void => {
    if (!supportedLanguages.includes(language)) {
        return;
    }

    localStorage.setItem('i18nextLng', language);
};

export const getCurrentLocale = () => {
    switch (i18nInstance.language) {
        case 'de': return 'de-DE';
        case 'nb': return 'nb-NO';
        default: return 'en-US';
    }
};

export const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string =>
    value.toLocaleString(getCurrentLocale(), options);

export const formatMoney = (value: number, currency: string): string => {
    return Intl.NumberFormat(getCurrentLocale(), {
        style: 'currency',
        currency: currency,
    }).format(value);
};
